import './styles.scss';

import { classes } from 'html-classes';
import { compact } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { CarouselIcon } from '~/components/CommonCarousel';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useDotButton } from '~/hooks/useDotButton';
import { useGlobal } from '~/hooks/useGlobal';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { userStore } from '~/stores';
import { Category } from '~/stores/CategoriesStore';
import { formatPriceWithCurrency } from '~/utils/formaters';

import { createProductUrl } from '../utils';

import styles from './BannerProductBlock.module.scss';

interface BannerProductBlockProps {
  category: Category;
}

const BannerProductBlock = ({ category }: BannerProductBlockProps) => {
  const { isMobile } = useGlobal();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  // TODO: figure out why products initially contains null values (that's why compact is used)
  const products = compact(
    category.subcategory.map(({ products }) => products.at(0)),
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const showArrows = !isMobile && products.length > 1;

  const carouselOpts = useMemo(
    () => ({
      align: 'start' as const,
      slidesToScroll: 1,
      containScroll: 'trimSnaps' as const,
    }),
    [],
  );

  useOnWarehouseChange(() => category.productsRequest.run());

  if (!products?.length && !category.productsRequest.isLoading) {
    return null;
  }

  return (
    <div className={classes([styles.root, 'banner-product-block'])}>
      <div className={classes([styles.container, 'banner-carousel carousel'])}>
        {!prevBtnDisabled && showArrows && (
          <CarouselIcon
            position="left"
            variant="fill"
            className={styles.leftControl}
            onClick={onPrevButtonClick}
          />
        )}
        <Carousel
          setApi={setApi}
          opts={carouselOpts}
          className={styles.carousel}
          dir={userStore.dir}
          key={userStore.dir}
        >
          <CarouselContent
            style={{
              gap: '20px',
            }}
          >
            {products.map(
              ({
                id,
                name,
                categoryName,
                priceFormatted,
                previewImageThumb,
                promoQuantityDiscountPrice,
                promoQuantityDiscountPriceFormatted,
                slug,
              }) => (
                <CarouselItem className={styles.slide} key={id}>
                  <Link
                    data-link={`banner-${slug}`}
                    to={createProductUrl({ id: slug })}
                    className={styles.link}
                  >
                    <div className={styles.product}>
                      <CloudflareResponseImage
                        src={previewImageThumb}
                        srcParams={{ width: 96 }}
                        srcSetParams={defaultSrcSetParams}
                        alt={name}
                        loading="lazy"
                      />
                    </div>
                    <div className={styles.description}>
                      <div className={styles.title}>{categoryName}</div>
                      <div className={styles.name}>{name}</div>
                      <div className={styles.price}>
                        <div className={styles.totalPrice}>
                          {formatPriceWithCurrency(priceFormatted)}
                        </div>
                        {!!promoQuantityDiscountPrice && (
                          <div className={styles.discount}>
                            <del>
                              {formatPriceWithCurrency(
                                promoQuantityDiscountPriceFormatted,
                              )}
                            </del>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              ),
            )}
          </CarouselContent>
        </Carousel>
        {!nextBtnDisabled && showArrows && (
          <CarouselIcon
            position="right"
            variant="fill"
            className={styles.rightControl}
            onClick={onNextButtonClick}
          />
        )}
      </div>

      {products.length > 1 && isMobile && (
        <div className={styles.carouselPagination}>
          <ul>
            {scrollSnaps.map((_, idx) => {
              return (
                <li key={idx}>
                  <span
                    role="button"
                    className={classes([
                      styles.carouselPagination__dot,
                      idx === selectedIndex && styles.active,
                    ])}
                    onClick={() => onDotButtonClick(idx)}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default observer(BannerProductBlock);
